import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CreditCard, Calendar, CheckCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const Billing = () => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await api.get('/subscriptions/me');
      setSubscription(response.data);
    } catch (error) {
      console.error('Subscription fetch error:', error);
      // Set default subscription data if API fails
      setSubscription({
        plan: 'trial',
        active: true,
        days_remaining: 365,
        monthly_payment_url: process.env.REACT_APP_FYGARO_MONTHLY_URL || '',
        yearly_payment_url: process.env.REACT_APP_FYGARO_YEARLY_URL || ''
      });
      if (error.response?.status !== 404) {
        toast.error(error.response?.data?.detail || 'Could not load subscription details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  const isTrialActive = subscription?.plan === 'trial' && subscription?.days_remaining > 0;

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-outfit font-bold text-ocean-900 mb-8">Billing & Subscription</h1>

        {/* Current Plan */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle>Current Plan</CardTitle>
                <CardDescription className="mt-2">
                  Manage your CTC Cargo Network subscription
                </CardDescription>
              </div>
              <Badge className={`${
                subscription?.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {subscription?.active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <CreditCard className="h-5 w-5 text-cyan-500" />
                <div>
                  <p className="font-semibold text-lg capitalize">
                    {subscription?.plan === 'trial' ? 'Free Trial' : `${subscription?.plan} Plan`}
                  </p>
                  {isTrialActive && (
                    <p className="text-sm text-slate-600">
                      {subscription?.days_remaining} days remaining in your free trial
                    </p>
                  )}
                </div>
              </div>

              {subscription?.trial_end && (
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="text-slate-600">
                      Trial ends: {new Date(subscription.trial_end).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}

              {isTrialActive && subscription?.days_remaining < 30 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mt-4">
                  <p className="text-orange-800 text-sm">
                    ⚠️ Your trial is ending soon. Choose a plan below to continue using CTC Cargo Network.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pricing Plans */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="border-2 border-cyan-500 relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-cyan-500 text-white px-4 py-1 rounded-full text-sm font-medium">
              Most Popular
            </div>
            <CardHeader>
              <CardTitle className="text-2xl">Monthly Plan</CardTitle>
              <CardDescription>
                <span className="text-4xl font-bold text-ocean-900">$99.99</span>
                <span className="text-slate-600">/month</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm">Unlimited RFQs & Quotes</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm">Real-time Messaging</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm">Freight Rate Insights</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm">Priority Support</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm">Cancel Anytime</span>
                </li>
              </ul>
              <Button
                onClick={() => handlePayment(subscription?.monthly_payment_url)}
                className="w-full bg-cyan-500 hover:bg-cyan-600"
                disabled={subscription?.plan === 'monthly' || !subscription?.monthly_payment_url}
                data-testid="monthly-plan-button"
              >
                {subscription?.plan === 'monthly' ? 'Current Plan' : (
                  <>
                    Select Monthly
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Yearly Plan</CardTitle>
              <CardDescription>
                <span className="text-4xl font-bold text-ocean-900">$699</span>
                <span className="text-slate-600">/year</span>
                <Badge className="ml-2 bg-green-100 text-green-700">Save $500</Badge>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm">Everything in Monthly</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm">Best Value - 2 Months Free</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm">One Simple Payment</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm">Priority Access to New Features</span>
                </li>
              </ul>
              <Button
                onClick={() => handlePayment(subscription?.yearly_payment_url)}
                className="w-full bg-ocean-900 hover:bg-ocean-800"
                disabled={subscription?.plan === 'yearly' || !subscription?.yearly_payment_url}
                data-testid="yearly-plan-button"
              >
                {subscription?.plan === 'yearly' ? 'Current Plan' : (
                  <>
                    Select Yearly
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Info Box */}
        <Card className="mt-6 bg-cyan-50 border-cyan-200">
          <CardContent className="p-6">
            <p className="text-sm text-cyan-900">
              <strong>Secure Payment:</strong> Payments are processed securely through Fygaro. After completing payment, your subscription will be automatically activated.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Billing;
